import React from 'react';
import { BarChart2, CalendarClock, AlertTriangle, Building2 } from 'lucide-react';

/**
 * Компонент секции амортизации и накладных расходов для калькулятора 3D-печати
 */
const OverheadsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-100 dark:border-orange-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-orange-500" />
        Амортизация и дополнительные расходы
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        {/* Амортизация принтера */}
        <div>
          <div className="mb-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeDepreciation"
                checked={formData.includeDepreciation}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Учитывать амортизацию принтера
              </span>
            </label>
          </div>
          
          {formData.includeDepreciation && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 pl-6 border-l-2 border-orange-200 dark:border-orange-700">
              <div>
                <label htmlFor="depreciationPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <CalendarClock className="w-4 h-4 mr-1 text-orange-500" />
                  Срок амортизации (месяцев)
                </label>
                <input
                  type="text"
                  id="depreciationPeriod"
                  name="depreciationPeriod"
                  value={formData.depreciationPeriod}
                  onChange={handleInputChange}
                  className={`form-input w-full ${errors.depreciationPeriod ? 'input-error' : ''}`}
                  placeholder="Введите срок"
                />
                {errors.depreciationPeriod && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.depreciationPeriod}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="printerMonthlyUse" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Использование в месяц (часов)
                </label>
                <input
                  type="text"
                  id="printerMonthlyUse"
                  name="printerMonthlyUse"
                  value={formData.printerMonthlyUse}
                  onChange={handleInputChange}
                  className={`form-input w-full ${errors.printerMonthlyUse ? 'input-error' : ''}`}
                  placeholder="Введите часы использования"
                />
                {errors.printerMonthlyUse && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.printerMonthlyUse}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Учет процента брака */}
        <div className="mt-2">
          <div className="mb-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeFailureRate"
                checked={formData.includeFailureRate}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Учитывать процент брака
              </span>
            </label>
          </div>
          
          {formData.includeFailureRate && (
            <div className="pl-6 border-l-2 border-orange-200 dark:border-orange-700 mt-3">
              <label htmlFor="failureRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1 text-orange-500" />
                Процент брака (%)
              </label>
              <input
                type="text"
                id="failureRate"
                name="failureRate"
                value={formData.failureRate}
                onChange={handleInputChange}
                className={`form-input w-full max-w-md ${errors.failureRate ? 'input-error' : ''}`}
                placeholder="Введите процент"
              />
              {errors.failureRate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.failureRate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Примерный процент неудачных печатей, которые приходится переделывать
              </p>
            </div>
          )}
        </div>

        {/* Накладные расходы */}
        <div className="mt-2">
          <div className="mb-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeOverheads"
                checked={formData.includeOverheads}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Учитывать накладные расходы
              </span>
            </label>
          </div>
          
          {formData.includeOverheads && (
            <div className="pl-6 border-l-2 border-orange-200 dark:border-orange-700 mt-3">
              <label htmlFor="overheadPercentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Building2 className="w-4 h-4 mr-1 text-orange-500" />
                Процент накладных расходов (%)
              </label>
              <input
                type="text"
                id="overheadPercentage"
                name="overheadPercentage"
                value={formData.overheadPercentage}
                onChange={handleInputChange}
                className={`form-input w-full max-w-md ${errors.overheadPercentage ? 'input-error' : ''}`}
                placeholder="Введите процент"
              />
              {errors.overheadPercentage && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.overheadPercentage}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Аренда помещения, обслуживание оборудования, административные расходы
              </p>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-4 bg-orange-100/50 dark:bg-orange-900/30 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
        <div className="flex items-start">
          <BarChart2 className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-3 mt-1 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-1">
              Зачем учитывать амортизацию?
            </h4>
            <p className="text-xs text-orange-600 dark:text-orange-400">
              Амортизация — важный компонент себестоимости, учитывающий постепенный износ оборудования. Стандартный срок амортизации 3D-принтера составляет 2-4 года (24-48 месяцев). При коммерческой 3D-печати рекомендуется устанавливать срок амортизации ближе к реальному сроку службы вашего оборудования.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverheadsSection;